<template>
  <div class="flex-1" style="position: relative;">
    <div
      class="w-full p-0 content"
      style="padding: 10px;"
      v-if="billingUser && user && billingUser.id !== user.id"
    >
      <div class="billing-user">
        <div class="billing-user-details">
          <span class="title">
            Current Billing Account ({{ activeArtist && activeArtist.name }}):
          </span>
          <span class="name" v-if="billingUser.first_name">
            {{ billingUser.first_name + " " + billingUser.last_name }}
          </span>
          <span class="email">{{ billingUser.email }}</span>
        </div>
      </div>
    </div>
    <template v-else>
      <Loader v-if="$apolloData.loading || submitting" />
      <template v-if="$apolloData.queries.billing_information.loading" />

      <div
        class="w-full p-0 content"
        style="padding: 10px;"
        v-show="!currentArtist"
      >
        <!-- v-if="showSubscriptionDetails || isPastDueSubscription" -->
        <div
          class="flex items-center justify-center w-full gap-4 py-5"
          v-if="isPastDueSubscription"
        >
          <template v-for="(artist, i) in artists">
            <img
              v-if="i < 2"
              :key="artist.id"
              class="w-16 h-16 rounded-full"
              :src="artist.avatar_url"
              :alt="artist.name"
            />
          </template>
          <span
            class="flex items-center justify-center w-16 h-16 overflow-hidden text-xl font-semibold bg-white rounded-full text-purple-background"
            v-if="artists.length > 2"
          >
            {{ artists.length - 2 }}+
          </span>
        </div>
        <div class="title-text">
          <p
            class="text-center title"
            v-if="inactiveSubscription && !subscribing"
          >
            Account
            {{ subscription.status === "Past Due" ? "Past Due" : "Suspended" }}
          </p>
          <p class="title" v-else>
            {{ isActiveEnterprise ? "Enterprise Plan" : "Plans and Payment" }}
          </p>
          <p class="subtitle" v-if="inactiveSubscription">
            <!-- Please make a payment. -->
          </p>
          <p class="subtitle" v-if="isActiveEnterprise">
            Your Enterprise plan is managed by ArtistHub.
          </p>
          <p class="subtitle" v-else-if="!inactiveSubscription">
            Manage your subscriptions and payment method.
          </p>
        </div>
        <div class="past-due" v-if="isPastDueSubscription">
          <div class="title">
            <i class="fa fa-exclamation-circle"></i>
            Issue charging card on file.
          </div>
          <div class="details">
            Please add a new payment method to protect your links and data.
          </div>
        </div>
        <div
          class="suspended-artist"
          v-if="
            !isActiveEnterprise && !activeArtist.current_plan && !subscribing
          "
        >
          <div class="artist-details">
            <span class="name" v-if="this.isAgency || this.isFamily">
              {{ activeArtist.name }} is currently suspended. Please select a
              plan.
            </span>
            <span class="name" v-else>
              {{ activeArtist.name }} has a billing issue. Please select a plan
              and verify your billing information. Links are unaffected, but
              please resolve ASAP.
            </span>
          </div>
        </div>

        <div class="billing-card" v-if="!isActiveEnterprise">
          <div class="card-top">
            <span class="card-title">Payment Method</span>
            <p class="card-text">
              Your default method is charged once a month for all active
              subscriptions.
            </p>
          </div>
          <div class="card-content">
            <div class="braintree-billing">
              <template>
                <div class="payment-container">
                  <div id="dropin-container"></div>
                  <div class="payment-footer">
                    <button
                      v-if="paymentButtonActive"
                      @click.prevent="onSuccessBillingUpdate"
                      class="button-default big"
                      :disabled="saving || paymentButtonDisabled"
                    >
                      <span>
                        {{ paymentButtonText }}
                      </span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="billing-card" v-if="isActiveEnterprise">
          <div class="card-top">
            <span class="card-title">Enterprise Plan</span>
            <p class="card-text">
              Your plan includes unlimited artist management and link creation.
            </p>
          </div>
          <div class="card-content">
            <button
              class="button-default enterprise-manage-button"
              type="button"
              @click="$intercom.showMessages()"
            >
              <span>Manage Enterprise Plan</span>
            </button>
          </div>
        </div>
        <div class="billing-card" v-else>
          <template v-if="!isAgency">
            <div class="flex items-center justify-between w-full">
              <span class="text-lg font-semibold text-white opacity-90"
                >Artist Plans</span
              >
              <span class="flex items-center justify-start gap-1">
                <label for="yearly" class="switch">
                  <input
                    v-model="yearly"
                    type="checkbox"
                    class="switch-input"
                    name="yearly"
                    id="yearly"
                    checked
                  />
                  <span
                    class="switch-background"
                    data-on="On"
                    data-off="Off"
                  ></span>
                  <span class="switch-handle"></span>
                </label>
                <label
                  class="text-xs font-bold select-none text-light-purple-text"
                  :class="yearly ? '' : 'opacity-50'"
                  for="yearly"
                  >Yearly Billing
                  <span v-if="sumYearlyOfDiscounts > 0"
                    >(${{ sumYearlyOfDiscounts }} Discount yearly)</span
                  ></label
                >
              </span>
            </div>
            <div class="artists">
              <div class="flex items-center justify-between w-full">
                <span class="text-sm text-lilac">Artist</span>
                <div class="flex items-center justify-end gap-12">
                  <span class="text-sm text-lilac">Current Plan</span>
                  <span class="text-sm text-right text-lilac w-28">Price</span>
                  <span class="flex w-16 h-1"></span>
                </div>
              </div>

              <div
                class="artist"
                :class="
                  !artist.active_plan && artist.current_plan ? 'disabled' : null
                "
                v-for="(artist, i) in artists"
                :key="i"
              >
                <div class="artist-details">
                  <img :src="artist.avatar_url" class="avatar" />
                  <span class="name">{{ artist.name }}</span>
                </div>
                <div class="flex items-center justify-end gap-12 subscription">
                  <span class="flex flex-col items-end gap-1">
                    <span
                      class="suspended-active"
                      v-if="!artist.active_plan && artist.current_plan"
                    >
                      Suspended
                    </span>

                    <span class="status" v-else>
                      {{ artist | getArtistPlan(subscription) }}
                    </span>
                    <span
                      v-if="artist.active_plan != artist.current_plan"
                      class="text-sm text-lilac"
                    >
                      {{
                        `${plans[artist.current_plan.split("_")[0]]} until
                                            ${getFormattedDate(
                                              subscription.next_billing_date
                                            )}`
                      }}
                    </span>
                  </span>

                  <span
                    class="flex items-center justify-end font-semibold status slashed-zero tabular-nums w-28"
                    v-if="artist"
                  >
                    <span
                      v-if="yearly && artist.active_plan"
                      class="mr-2 line-through text-main-red animate-fadeIn"
                    >
                      ${{ artist | monthlyPrice(planPrices, addOnsAmount) }}
                    </span>
                    <span>
                      ${{
                        artist
                          | calculatePrice(yearly, planPrices, addOnsAmount)
                      }}
                    </span>
                    <span class="text-lilac ">/{{ yearly ? "yr" : "mo" }}</span>
                  </span>

                  <Dropdown
                    v-if="artist.active_plan && !isTrial"
                    :options="[
                      {
                        name: 'Change Plan',
                        callback: () => {
                          changeArtistPlan(artist);
                        }
                      },
                      {
                        name: 'Suspend artist',
                        callback: () => {
                          suspendArtist(artist);
                        }
                      }
                    ]"
                  >
                    <span
                      class="flex items-center justify-center p-2 rounded-full"
                    >
                      <svg
                        width="14"
                        height="4"
                        viewBox="0 0 14 4"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.75 2C3.75 2.96875 2.9375 3.75 2 3.75C1.03125 3.75 0.25 2.96875 0.25 2C0.25 1.0625 1.03125 0.25 2 0.25C2.9375 0.25 3.75 1.0625 3.75 2ZM8.75 2C8.75 2.96875 7.9375 3.75 7 3.75C6.03125 3.75 5.25 2.96875 5.25 2C5.25 1.0625 6.03125 0.25 7 0.25C7.9375 0.25 8.75 1.0625 8.75 2ZM10.25 2C10.25 1.0625 11.0312 0.25 12 0.25C12.9375 0.25 13.75 1.0625 13.75 2C13.75 2.96875 12.9375 3.75 12 3.75C11.0312 3.75 10.25 2.96875 10.25 2Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </Dropdown>
                  <Dropdown
                    v-else-if="artist.current_plan && !isTrial"
                    :options="[
                      {
                        name: 'Change Plan',
                        callback: () => {
                          changeArtistPlan(artist);
                        }
                      },
                      {
                        name: 'Restore artist',
                        callback: () => {
                          restoreArtist(artist);
                        }
                      }
                    ]"
                  >
                    <span
                      class="flex items-center justify-center p-2 rounded-full"
                    >
                      <svg
                        width="14"
                        height="4"
                        viewBox="0 0 14 4"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.75 2C3.75 2.96875 2.9375 3.75 2 3.75C1.03125 3.75 0.25 2.96875 0.25 2C0.25 1.0625 1.03125 0.25 2 0.25C2.9375 0.25 3.75 1.0625 3.75 2ZM8.75 2C8.75 2.96875 7.9375 3.75 7 3.75C6.03125 3.75 5.25 2.96875 5.25 2C5.25 1.0625 6.03125 0.25 7 0.25C7.9375 0.25 8.75 1.0625 8.75 2ZM10.25 2C10.25 1.0625 11.0312 0.25 12 0.25C12.9375 0.25 13.75 1.0625 13.75 2C13.75 2.96875 12.9375 3.75 12 3.75C11.0312 3.75 10.25 2.96875 10.25 2Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </Dropdown>
                  <Dropdown
                    v-else
                    :options="[
                      {
                        name: 'Change Plan',
                        callback: () => {
                          changeArtistPlan(artist);
                        }
                      }
                    ]"
                  >
                    <span
                      class="flex items-center justify-center p-2 rounded-full"
                    >
                      <svg
                        width="14"
                        height="4"
                        viewBox="0 0 14 4"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.75 2C3.75 2.96875 2.9375 3.75 2 3.75C1.03125 3.75 0.25 2.96875 0.25 2C0.25 1.0625 1.03125 0.25 2 0.25C2.9375 0.25 3.75 1.0625 3.75 2ZM8.75 2C8.75 2.96875 7.9375 3.75 7 3.75C6.03125 3.75 5.25 2.96875 5.25 2C5.25 1.0625 6.03125 0.25 7 0.25C7.9375 0.25 8.75 1.0625 8.75 2ZM10.25 2C10.25 1.0625 11.0312 0.25 12 0.25C12.9375 0.25 13.75 1.0625 13.75 2C13.75 2.96875 12.9375 3.75 12 3.75C11.0312 3.75 10.25 2.96875 10.25 2Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </Dropdown>
                </div>
              </div>
            </div>
          </template>

          <div class="artists">
            <div
              v-if="!isAgency"
              class="flex items-center justify-between w-full"
            >
              <span class="text-sm text-lilac"></span>
              <div class="flex items-center justify-end gap-12">
                <span class="text-sm text-lilac"></span>
                <span class="text-sm text-right text-lilac w-28">Total</span>
                <span class="flex w-16 h-1"></span>
              </div>
            </div>

            <div
              class="flex items-center justify-between px-6 py-4 bg-purple-background rounded-ah"
            >
              <div class="artist-details">
                <span class="font-semibold text-white">Next Charge</span>
              </div>
              <div class="flex items-center justify-end gap-12 subscription">
                <span class="flex flex-col items-end gap-1">
                  <span class="flex items-center justify-start gap-2">
                    <span
                      v-if="agencySubscriptionManual"
                      class="flex items-center justify-end font-semibold status slashed-zero tabular-nums w-28"
                    >
                      <span>
                        ${{
                          agencySubscriptionManual.yearly
                            ? agencySubscriptionManual.yearly
                            : agencySubscriptionManual.monthly
                        }}</span
                      >
                      <span class="text-lilac ">
                        /{{
                          agencySubscriptionManual.yearly ? "yr" : "mo"
                        }}</span
                      >
                    </span>
                    <span
                      v-else-if="
                        !agencyHasSavedMoney && agencySubscriptionTotal
                      "
                      class="flex items-center justify-end font-semibold status slashed-zero tabular-nums"
                    >
                      <span>${{ agencySubscriptionTotal }}</span>
                      <span class="text-lilac "
                        >/{{ yearly ? "yr" : "mo" }}</span
                      >
                    </span>
                    <span
                      v-else
                      class="flex items-center justify-end font-semibold status slashed-zero tabular-nums w-28"
                      :class="
                        agencyHasSavedMoney ? 'line-through animate-fadeIn' : ''
                      "
                    >
                      <span
                        :class="agencyHasSavedMoney ? 'text-main-red' : null"
                        >${{ yearly ? total.yearly : total.monthly }}</span
                      >
                      <span class="text-lilac "
                        >/{{ yearly ? "yr" : "mo" }}</span
                      >
                    </span>
                    <span
                      v-if="agencyHasSavedMoney"
                      class="flex items-center justify-end font-semibold status slashed-zero tabular-nums"
                    >
                      <span>${{ agencyHasSavedMoney }}</span>
                      <span class="text-lilac "
                        >/{{ yearly ? "yr" : "mo" }}</span
                      >
                    </span>
                  </span>
                  <span class="text-xs text-lilac" v-if="isPastDueSubscription">
                    Due Now
                  </span>
                  <span
                    class="text-xs text-lilac"
                    v-else-if="
                      nextBillingDate &&
                        !isCanceledSubscription &&
                        !agencySubscriptionManual
                    "
                  >
                    {{ getFormattedDate(nextBillingDate) }}
                  </span>
                </span>

                <!-- <span class="flex h-1 w-9"></span> -->
              </div>
            </div>
          </div>

          <div
            v-if="wantToChangeInterval"
            class="flex items-center justify-end w-full py-4"
          >
            <button
              @click="updateBillingInterval(yearly ? 'yearly' : 'monthly')"
              :disabled="$apolloData.loading || submitting"
              class="button-default big button-default--outline"
            >
              <span>
                {{
                  !($apolloData.loading || submitting)
                    ? "Update Billing Interval"
                    : "Loading..."
                }}
              </span>
            </button>
          </div>
        </div>
        <div
          class="billing-card"
          v-if="!isActiveEnterprise && invoiceHistory.length && !subscribing"
        >
          <div class="card-top">
            <span class="card-title">Invoices</span>
          </div>
          <div class="invoices">
            <div
              class="invoice"
              v-for="(invoice, i) in invoiceHistory"
              :key="i"
            >
              <div class="payment-method">
                <FileIcon />
                <div class="details">
                  <span class="title">{{ invoice.title }}</span>
                </div>
              </div>
              <div class="subscription">
                <div class="invoice-date">
                  {{ invoice.period }}
                </div>
                <div class="details">
                  <span class="total">${{ invoice.amount }}</span>
                </div>
                <button
                  class="button-default big"
                  @click="downloadInvoice(invoice)"
                >
                  <font-awesome-icon :icon="['fas', 'download']" />
                  <span>Invoice</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <SnackBar />
      </div>
      <UpdatedUserBilling
        v-if="currentArtist && (isCanceledSubscription || noSubscription)"
        :artist="currentArtist"
        :actionType="actionType"
        :subscription="subscription"
        :subscribing="subscribing"
        :user="user"
        @close="closeArtistBilling"
      />
    </template>
    <UpdateArtistPlan
      v-if="openPlanChangeModal"
      :artist="artistToChangePlan"
      :subscription="subscription"
      :subscribing="subscribing"
      :user="user"
      :yearly="yearly"
      @close="closeChangeArtistPlan"
    />
  </div>
</template>

<script>
import moment from "moment";
import Swal from "sweetalert2";
import dropin from "braintree-web-drop-in";
import { jsPDF } from "jspdf";
import Visa from "@/assets/img/svg/visa.svg";
import Mastercard from "@/assets/img/svg/mastercard.svg";
import Amex from "@/assets/img/svg/amex.svg";
import JCB from "@/assets/img/svg/jcb.svg";
import Discover from "@/assets/img/svg/discover.svg";
import UpdateArtistPlan from "@/components/Custom/UpdateArtistPlan.vue";
import {
  Q_PRO_PRICING,
  Q_BILLING_INFORMATION,
  Q_GET_TRANSACTIONS,
  Q_GET_USER,
  M_BRAINTREE_CLIENT_TOKEN,
  M_BRAINTREE_CHANGE_BILLING_INTERVAL,
  // M_BRAINTREE_RESUBSCRIBE,
  M_BRAINTREE_SUBSCRIBE,
  M_BRAINTREE_RETRY_CHARGE,
  M_BRAINTREE_SET_DEFAULT_PAYMENT_METHOD,
  M_BRAINTREE_CHANGE_ARTIST_PLAN
} from "@/graphql";
import FileIcon from "../../../../public/img/icons/file.svg";
import "@/assets/scss/custom/vendors-extensions/_sweetalert.scss";
import "@/assets/scss/custom/vendors-extensions/_braintree.scss";
import SnackBar from "@/components/Custom/SnackBar";
// import BillingArtist from "@/components/Custom/BillingArtist";
import UpdatedUserBilling from "@/components/Custom/UpdatedUserBilling";
import Loader from "@/components/Custom/Loader";
import Dropdown from "@/components/Inputs/Dropdown";
export default {
  name: "Billing",
  components: {
    SnackBar,
    // BillingArtist,
    FileIcon,
    Loader,
    UpdatedUserBilling,
    Dropdown,
    UpdateArtistPlan
  },
  data() {
    return {
      braintreeInstance: null,
      braintreeView: null,
      cardValid: false,
      defaultPaymentMethod: null,
      saving: false,
      braintreeInitRetries: 0,
      maxBraintreeRetries: 3,
      plans: {
        bio: "Smart Bio",
        pro: "Pro",
        max: "Max"
      },
      billing_intervals: {
        monthly: "Monthly",
        yearly: "Yearly"
      },
      subscriptionFetched: false,
      subscription: {},
      artists: [],
      currentArtist: null,
      invoiceHistory: [],
      submitting: false,
      new_billing_interval: "monthly",
      subscribing: false,
      cardBrands: [
        {
          name: "Visa",
          icon: Visa
        },
        {
          name: "Mastercard",
          icon: Mastercard
        },
        {
          name: "American Express",
          icon: Amex
        },
        {
          name: "JCB",
          icon: JCB
        },
        {
          name: "Discover",
          icon: Discover
        }
      ],
      proPricing: null,
      actionType: null,
      yearly: false,
      planPrices: {
        pro: { monthly: 19, yearly: 190 },
        bio: { monthly: 9, yearly: 90 },
        max: { monthly: 29, yearly: 290 },
        suspended: { monthly: 0, yearly: 0 }
      },
      artistToChangePlan: {},
      openPlanChangeModal: false,
      selectedArtist: this.$store.getters.getArtist
    };
  },
  apollo: {
    proPricing: {
      query: Q_PRO_PRICING,
      variables() {
        return {
          user_id: this.user.id
        };
      },
      skip() {
        return !this.user;
      },
      update(data) {
        return data.users_by_pk.pro_pricing;
      },
      error(error) {
        console.error(error);
      },
      fetchPolicy: "no-cache"
    },
    billing_information: {
      query: Q_BILLING_INFORMATION,
      client: "localClient",
      variables() {
        return {};
      },
      result({ data }) {
        this.subscriptionFetched = true;
        if (!data.billing_information) return;
        if (!this.user || !this.user.artists) return;

        const { artists, ...subscription } = data.billing_information;

        this.yearly = data.billing_information?.billing_interval == "yearly";

        this.artists = [];

        this.user.artists.forEach(artist => {
          const foundArtist = artists.find(a => a.id === artist.id);
          if (foundArtist) {
            this.artists.push(artist);
          }
        });

        this.artists.sort((a, b) => a.name.localeCompare(b.name));

        this.subscription = subscription;

        this.$store.dispatch("setArtist", this.selectedArtist);

        this.$nextTick(() => {
          this.checkArtistSubscription();
        });
      },
      error(error) {
        console.error(error);
      }
    },
    braintree_get_transactions: {
      client: "localClient",
      query: Q_GET_TRANSACTIONS,
      result({ data }) {
        try {
          const transactions = JSON.parse(
            data.braintree_get_transactions.transactions
          );

          const sortedTransactions = transactions
            .filter(x => x.status === "settled" && x.type === "sale")
            .sort((a, b) => {
              return new Date(b.createdAt) - new Date(a.createdAt);
            });

          this.invoiceHistory = sortedTransactions.map((x, i) => {
            const nextTransaction = sortedTransactions[i + 1];
            const isProrated =
              nextTransaction?.subscription?.billingPeriodEndDate &&
              new Date(x.createdAt) <
                new Date(nextTransaction.subscription.billingPeriodEndDate);

            const addOns = !isProrated
              ? x.addOns
                  .sort((a, b) => a.id.localeCompare(b.id))
                  .map(
                    ({ id, quantity }) =>
                      `${quantity}x ${this.plans[id.split("_")[0]]}`
                  )
              : [];

            const title =
              (addOns.join(", ") || `1x ${x.planId}`) +
              ` (Billed ${
                this.billing_intervals[x.planId]
                  ? this.billing_intervals[x.planId]
                  : x.planId?.includes("yearly")
                  ? "Yearly"
                  : "Monthly"
              }${isProrated ? " - Prorated" : ""})`;

            return {
              ...x,
              id: x.globalId,
              title,
              subtitle: null,
              amount: x.amount,
              date: moment(x.createdAt).format("DD MMM YYYY"),
              period: `${moment(x.subscription.billingPeriodStartDate).format(
                "DD MMM YYYY"
              )} – ${moment(x.subscription.billingPeriodEndDate).format(
                "DD MMM YYYY"
              )}`
            };
          });
        } catch (error) {
          console.error(error);
        }
      },
      error(error) {
        console.error(error);
      }
    },
    get_current_user_data: {
      client: "localClient",
      query: Q_GET_USER,
      result({ data }) {
        const user = data.get_current_user_data.user;
        this.$store.dispatch("setUser", user);
        this.$store.dispatch(
          "setArtist",
          this.selectedArtist ?? { ...user.artists[0] }
        );
      },
      error(error) {
        console.error(error);
      }
    }
  },
  async mounted() {
    try {
      await this.$apollo.queries?.user?.refetch();
      this.selectedArtist = this.activeArtist;

      if (this.isActiveEnterprise) return;

      await this.initializeBraintreeDropin();
    } catch (error) {
      console.error(error);
    }
  },
  watch: {
    subscription(val) {
      if (!val || !val.status) return;

      const { artist_id } = this.$route.query;

      const nextBillingDate = new Date(val?.next_billing_date);
      const now = Date();

      if (val.status === "Past Due" || this.currentArtist) return;

      if (artist_id) {
        if (!this.artists || this.artists.length === 0) return;

        const updatedQuery = { ...this.$route.query };
        delete updatedQuery.artist_id;
        this.$router.replace({ query: updatedQuery });

        if (
          ["Active", "Pending"].includes(val.status) &&
          nextBillingDate < now
        ) {
          const artist = this.artists.find(x => x.id === artist_id);
          if (artist) {
            this.currentArtist = {
              ...artist,
              active_plan: artist.active_plan || ""
            };
          }
          return;
        }
      }
      if (nextBillingDate < now) return this.checkArtistSubscription();
    },
    activeArtist(val) {
      if (!val) return;
      this.selectedArtist = val;
      this.checkArtistSubscription();
    }
  },
  filters: {
    getArtistPlan(artist) {
      const plans = {
        pro: "Pro",
        bio: "Smart Bio",
        max: "Max",
        suspended: "Suspended"
      };

      const active = artist.active_plan?.split("_")[0] ?? "suspended";

      return plans[active];
    },

    formatDate(date) {
      return moment(date).format("MMMM Do, YYYY");
    },
    calculatePrice(artist, yearly, planPrices, addOnsAmount) {
      const activePlan = artist.active_plan?.split("_")[0] || "suspended";
      const price = yearly
        ? Math.ceil(planPrices[activePlan].yearly / 12)
        : planPrices[activePlan]?.monthly;

      const addOnAmount = Number(addOnsAmount[activePlan] ?? 0);

      return addOnAmount ?? price;
    },

    monthlyPrice(artist, planPrices, addOnsAmount) {
      const activePlan = artist.active_plan?.split("_")[0] || "suspended";
      const price = planPrices[activePlan]?.monthly;

      const addOnAmount = Number(addOnsAmount[activePlan] ?? 0);

      return addOnAmount ?? price;
    }
  },
  computed: {
    activeArtist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    billingUser() {
      return this.$store.getters.getBillingUser;
    },
    snack() {
      return this.$store.getters.getSnack;
    },
    isAgency() {
      return this.user?.plan === "agency";
    },
    isActiveEnterprise() {
      return (
        this.user?.plan === "agency" &&
        this.user?.braintree_subscription_status === "Active"
      );
    },
    isFamily() {
      return this.user?.plan === "family";
    },
    showSubscriptionDetails() {
      return (
        (["monthly", "yearly"].includes(this.subscription.billing_interval) ||
          this.isAgency ||
          this.isFamily) &&
        !this.inactiveSubscription &&
        !this.subscribing
      );
    },
    proPlanPrice() {
      return {
        monthly:
          "monthly" === this.subscription.billing_interval
            ? this.proPricing
            : 19,
        yearly:
          "yearly" === this.subscription.billing_interval
            ? this.proPricing
            : 190
      };
    },
    addOnsQuantity() {
      const artists = this.artists || [];
      return {
        bio:
          (this.subscription?.status !== "Canceled" &&
            this.subscription?.add_ons?.bio?.quantity) ||
          artists.filter(x => x.active_plan === "bio").length,
        pro:
          (this.subscription?.status !== "Canceled" &&
            this.subscription?.add_ons?.pro?.quantity) ||
          artists.filter(x => x.active_plan === "pro").length,
        max:
          (this.subscription?.status !== "Canceled" &&
            this.subscription?.add_ons?.max?.quantity) ||
          artists.filter(x => x.active_plan === "max").length
      };
    },
    addOnsAmount() {
      return {
        bio:
          this.subscription.status !== "Canceled" &&
          this.subscription?.add_ons?.bio?.amount,
        pro:
          this.subscription.status !== "Canceled" &&
          this.subscription?.add_ons?.pro?.amount,
        max:
          this.subscription.status !== "Canceled" &&
          this.subscription?.add_ons?.max?.amount
      };
    },
    trialTotal() {
      return {
        monthly:
          this.addOnsQuantity.bio * 6 +
          this.addOnsQuantity.pro * this.proPlanPrice?.monthly,
        yearly:
          this.addOnsQuantity.bio * 60 +
          this.addOnsQuantity.pro * this.proPlanPrice.yearly
      };
    },
    nextBillingDate() {
      return (
        this.subscription?.next_billing_date ||
        this.billingUser?.first_billing_date
      );
    },
    trialEnded() {
      return (
        this.billingUser.braintree_subscription_status === "Trial" &&
        moment(this.nextBillingDate).isBefore(moment())
      );
    },
    total() {
      const monthly =
        this.addOnsQuantity.bio *
          (this.addOnsAmount.bio ?? this.planPrices["bio"]?.monthly) +
        this.addOnsQuantity.pro *
          (this.addOnsAmount.pro ?? this.planPrices["pro"]?.monthly) +
        this.addOnsQuantity.max *
          (this.addOnsAmount.max ?? this.planPrices["max"]?.monthly);

      const yearly =
        this.addOnsQuantity.bio *
          (this.addOnsAmount.bio ?? this.planPrices["bio"].yearly) +
        this.addOnsQuantity.pro *
          (this.addOnsAmount.pro ?? this.planPrices["pro"].yearly) +
        this.addOnsQuantity.max *
          (this.addOnsAmount.max ?? this.planPrices["max"].yearly);

      return {
        monthly: monthly,
        yearly: yearly
      };
    },
    yearlyDiscount() {
      return this.total?.monthly * 12 - this.total.yearly;
    },
    yearlyDiscountTrial() {
      return this.trialTotal?.monthly * 12 - this.trialTotal.yearly;
    },
    isTrial() {
      return (
        (this.user.braintree_subscription_status == "Trial" ||
          !this.user.braintree_subscription_status) &&
        this.user.plan != "family"
      );
    },
    inactiveSubscription() {
      return (
        ["Canceled", "Past Due", "Expired"].includes(
          this.subscription.status
        ) && !this.isFamily
      );
    },
    isPastDueSubscription() {
      return this.subscription.status == "Past Due";
    },
    isCanceledSubscription() {
      return this.subscription.status == "Canceled";
    },
    noSubscription() {
      return !this.subscription.status;
    },
    paymentButtonActive() {
      if (!this.subscriptionFetched) return false;
      if (this.braintreeView === "card") return true;
      if (this.noSubscription || this.inactiveSubscription) return true;
      if (this.defaultPaymentMethodUpdated) return true;
      return false;
    },
    paymentButtonText() {
      if (this.noSubscription) return "Start Subscription";
      else if (this.inactiveSubscription) return "Reactivate Subscription";
      else if (this.isPastDueSubscription) return `Retry Charge`;
      // if (this.inactiveSubscription) return `Resubscribe`;
      // return `Pay now ($${this.subscription.total})`;
      else if (this.defaultPaymentMethodUpdated)
        return "Set as Default Payment Method";
      else return "Add Payment Method";
    },
    paymentButtonDisabled() {
      if (this.braintreeView === "card" && !this.cardValid) return true;
      if (this.noSubscription)
        return !this.new_billing_interval || this.submitting;
      if (this.inactiveSubscription) return this.submitting;
      return !this.braintreeInstance;
    },
    defaultPaymentMethodUpdated() {
      return (
        this.braintreeInstance?._model?._paymentMethods?.length > 1 &&
        this.defaultPaymentMethod &&
        this.defaultPaymentMethod !==
          this.braintreeInstance?._model?._activePaymentMethod?.nonce
      );
    },
    sumYearlyOfDiscounts() {
      let sum = 0;
      const userArtists = this.user?.artists || [];
      userArtists.forEach(artist => {
        if (!artist.current_plan) return;
        sum += this.planPrices[artist.active_plan]?.monthly * 2;
      });
      return Math.ceil(sum);
    },
    wantToChangeInterval() {
      return this.yearly !== (this.subscription?.billing_interval === "yearly");
    },
    agencyHasSavedMoney() {
      if (
        this.isAgency &&
        this.subscription &&
        parseInt(this.subscription.total ?? 0) < this.total?.monthly
      )
        return parseInt(this.subscription?.total);
      return null;
    },
    agencySubscriptionManual() {
      if (!this.subscription?.status && (this.isAgency || this.isFamily))
        return {
          monthly: this.billingUser.monthly_charge,
          yearly: this.billingUser.yearly_charge
        };
      return null;
    },
    agencySubscriptionTotal() {
      if (this.isAgency && this.subscription?.total)
        return parseInt(this.subscription.total);
      return null;
    }
  },
  methods: {
    async initializeBraintreeDropin() {
      try {
        const dropinContainer = document.getElementById("dropin-container");
        if (!dropinContainer) {
          if (this.braintreeInitRetries >= this.maxBraintreeRetries) {
            this.$store.dispatch("snack", {
              type: "error",
              text:
                "Payment form failed to load. Please refresh the page or contact support."
            });
            return;
          }

          this.braintreeInitRetries++;
          setTimeout(() => {
            this.initializeBraintreeDropin();
          }, 500);
          return;
        }

        this.braintreeInitRetries = 0;

        const result = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_BRAINTREE_CLIENT_TOKEN
        });

        const options = {
          authorization: result.data.braintree_client_token.token,
          selector: "#dropin-container",
          defaultFirst: true,
          card: {
            cvv: {
              required: true
            },
            overrides: {
              styles: {
                input: {
                  color: "white"
                },
                ":focus": {
                  color: "white"
                }
              },
              fields: {
                number: {
                  placeholder: "1234 1234 1234 1234"
                }
              }
            }
          }
        };

        dropin.create(options, (error, instance) => {
          if (error) {
            console.error(error);
            return;
          }

          this.braintreeInstance = instance;
          this.defaultPaymentMethod = this.braintreeInstance?._model?._activePaymentMethod?.nonce;

          this.braintreeInstance.on("changeActiveView", event => {
            this.braintreeView = event.newViewId;
          });

          this.braintreeInstance.on("card:validityChange", event => {
            const isValid = Object.values(event.fields).every(x => x.isValid);

            this.cardValid = isValid;
          });
        });
      } catch (error) {
        console.error(error);
      }
    },
    checkArtistSubscription() {
      if (!this.activeArtist || !this.activeArtist.id) return;
      if (!this.artists || this.artists.length === 0) return;

      if (!this.activeArtist.active_plan) {
        const artist = this.artists.find(x => x.id === this.activeArtist.id);

        if (!artist) return;

        const hasActiveSubscription = ["Active", "Pending"].includes(
          this.subscription.status
        );

        if (hasActiveSubscription) {
          this.currentArtist = null;
        } else {
          this.subscribing = !this.isFamily;
          this.currentArtist = {
            ...artist,
            active_plan: artist.active_plan || ""
          };
        }
      } else {
        this.currentArtist = null;
      }
    },
    async onSuccessBillingUpdate() {
      if (!this.braintreeInstance) {
        return this.$store.dispatch("snack", {
          type: "error",
          text: `Payment system not initialized. Please refresh the page.`
        });
      }

      this.braintreeInstance.requestPaymentMethod(async (err, payload) => {
        if (err) return console.error(err);

        let nonce = payload.nonce;
        if (!nonce) return;

        if (this.isPastDueSubscription) {
          try {
            this.submitting = true;
            let response = await this.$apollo.mutate({
              client: "localClient",
              mutation: M_BRAINTREE_RETRY_CHARGE,
              variables: {
                braintree_nonce: nonce
              }
            });

            const { user } = response.data.braintree_retry_charge;
            this.submitting = false;

            if (user) {
              this.defaultPaymentMethod = nonce;
              await this.$store.dispatch("setUser", user);
              await this.$apollo.queries.get_current_user_data.refetch();
              return this.$apollo.queries.billing_information.refetch();
            } else {
              return this.$store.dispatch("snack", {
                type: "error",
                text: `Oops! Something went wrong. Please try again.`
              });
            }
          } catch (err) {
            console.log(err);
            this.submitting = false;
            return this.$store.dispatch("snack", {
              type: "error",
              text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
            });
          }
        } else if (
          (this.noSubscription || this.inactiveSubscription) &&
          !this.isAgency &&
          !this.isFamily
        ) {
          try {
            this.submitting = true;
            const artistsToSubscribe = this.artists.flatMap(x => {
              return !x.active_plan
                ? []
                : [
                    {
                      id: x.id,
                      selected_plan: x.active_plan
                    }
                  ];
            });

            let response = await this.$apollo.mutate({
              client: "localClient",
              mutation: M_BRAINTREE_SUBSCRIBE,
              variables: {
                braintree_nonce: nonce,
                yearly_plan: this.new_billing_interval === "yearly",
                artists: artistsToSubscribe
              }
            });

            const { user } = response.data.braintree_subscribe;
            this.$store.commit("setUser", user);
            this.defaultPaymentMethod = nonce;
            this.submitting = false;
            this.subscribing = false;
            await this.$apollo.queries.get_current_user_data.refetch();
            return this.$apollo.queries.billing_information.refetch();
          } catch (err) {
            console.log(err);
            this.submitting = false;
            return this.$store.dispatch("snack", {
              type: "error",
              text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
            });
          }
        }

        this.submitting = true;
        try {
          await this.$apollo.mutate({
            client: "localClient",
            mutation: M_BRAINTREE_SET_DEFAULT_PAYMENT_METHOD,
            variables: {
              braintree_nonce: nonce
            }
          });

          this.defaultPaymentMethod = nonce;
          return (this.submitting = false);
        } catch (err) {
          this.submitting = false;
        }
      });
    },
    goToArtist(artist) {
      this.currentArtist = {
        ...artist,
        active_plan: artist.active_plan || ""
      };
    },
    async updateBillingInterval(interval) {
      try {
        const intervalText = this.billing_intervals[interval].toLowerCase();

        const confirmUpdate = await Swal.fire({
          title: `Are you sure?`,
          html: `<p class="description">You are switching to a ${intervalText} subscription.</p>`,
          type: "warning",
          showCloseButton: true,
          showCancelButton: true,
          confirmButtonText: "<span>Update</span>",
          cancelButtonText: "<span>Cancel</span>",
          confirmButtonClass: "button-danger big",
          cancelButtonClass: "button-secondary big",
          buttonsStyling: false
        });

        if (!confirmUpdate.value) return;

        this.submitting = true;
        let response = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_BRAINTREE_CHANGE_BILLING_INTERVAL,
          variables: {
            yearly_plan: interval === "yearly"
          }
        });
        const { success } = response.data.braintree_change_billing_interval;

        this.submitting = false;

        if (success) {
          await this.$store.dispatch("setBillingInterval", interval);
          await this.$apollo.queries.get_current_user_data.refetch();
          return this.$apollo.queries.billing_information.refetch();
        } else {
          await this.$store.dispatch("snack", {
            type: "error",
            text: `Oops! Something went wrong. Please try again.`
          });
        }
      } catch (err) {
        console.log(err);
        this.submitting = false;
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      }
    },
    async closeArtistBilling(submitted, success, errorText) {
      this.subscribing = false;

      this.currentArtist = null;

      if (!submitted) return;

      if (!success) {
        return this.$store.dispatch("snack", {
          type: "error",
          text: errorText || `Oops! Couldn't update the artist's plan`
        });
      }

      await this.$store.dispatch("snack", {
        text: "Artist plan updated successfully"
      });
      await this.$apollo.queries.get_current_user_data.refetch();
      return this.$apollo.queries.billing_information.refetch();
    },
    async downloadInvoice(tx) {
      const doc = new jsPDF({
        orientation: "p",
        unit: "px",
        format: [450, 450]
      });

      const fetchFont = async () => {
        try {
          const response = await fetch(
            "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf"
          );
          const buffer = await response.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          const chunkSize = 0x8000;
          let binary = "";
          for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.slice(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
          }
          return btoa(binary);
        } catch (err) {
          console.error("Error fetching font:", err);
          return null;
        }
      };

      const fontBase64 = await fetchFont();
      if (!fontBase64) {
        console.error("Could not load font file");
        return;
      }

      doc.addFileToVFS("Roboto.ttf", fontBase64);
      doc.addFont("Roboto.ttf", "Roboto", "normal");

      doc
        .setFont("Roboto", "bold")
        .setFontSize(15)
        .text("ArtistHub", 10, 40)
        .setFont("Roboto", "normal")
        .text("7665 TRENTON DR", 10, 55)
        .text("LAKE WORTH, FL 33467", 10, 70);

      let text = null;
      if (this.user.email === "noreply@11x11.io") {
        text = `
          STRIVE TO CREATE MEDIA S.R.L
          CUI 42390073
          JUD. CLUJ, SAT FLOREŞTI COM. FLOREŞTI, STR. AVRAM IANCU, NR.454A, SC.1, AP.113D
          VAT: RO42390073
          J12/976/2020
      `;
      } else if (
        ["vito.petan@manspire.eu", "vito.petan@outlook.com"].includes(
          this.user.email
        )
      ) {
        text = `
          VAT ID: SI33613656
          Company name: MNSPR d.o.o.
          Company address: Slovenska Cesta 16, 1000 Ljubljana, Slovenia
      `;
      } else if (this.user.email === "info@paraisorecords.be") {
        text = `
          VDW Productions SRL
          Rue Cockerill 164
          4100 Seraing, België
          BE0788.265.055
      `;
      } else if (this.user.email === "info@gardemusic.com") {
        text = `
          Company code: 305602483
          VAT: LT100019962418
          Girulių g. 10-201
          Vilnius LT-12112
          Lithuania
        `;
      }

      let end = 70;
      if (text) {
        const lines = text
          .trim()
          .split("\n")
          .map(line => line.trim());
        const maxWidth = 200;

        doc.setFontSize(15).setFont("Roboto", "normal");

        let currentY = 40;
        const lineHeight = 15;

        for (const line of lines) {
          if (line) {
            const splitLines = doc.splitTextToSize(line, maxWidth);
            doc.text(splitLines, 225, currentY);
            currentY += lineHeight * splitLines.length;
          }
        }

        end = currentY;
      }

      const offset = end - 70;

      doc.setFontSize(20).setFont("Roboto", "normal");
      doc.text(
        `${this.billing_intervals[this.subscription.billing_interval]} Invoice`,
        10,
        110 + offset
      );
      doc.text(tx.period, 10, 130 + offset);
      doc.text("INVOICE #" + tx.id, 10, 150 + offset);
      doc.setFontSize(10);
      doc.text("Prepared for", 10, 190 + offset);
      doc.text("Date", 375, 190 + offset);
      doc.setFontSize(15);
      doc.text(this.user.email, 10, 205 + offset);
      doc.text(new Date(tx.date).toLocaleDateString(), 375, 205 + offset);
      doc.setFontSize(10);
      doc.text("Item", 10, 235 + offset);
      doc.text("Price", 375, 235 + offset);
      doc.setFontSize(15);
      doc.text(tx.title, 10, 250 + offset);
      doc.text("$" + tx.amount, 375, 250 + offset);
      doc.save("invoice-" + tx.id + ".pdf");
    },
    async suspendArtist(artist) {
      const confirmUpdate = await Swal.fire({
        title: `Are you sure?`,
        html: `<p class="description">You are about to suspend <b style="color: #C5C5FF">${artist.name}</b>'s subscription.</p>`,
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "<span>Suspend</span>",
        cancelButtonText: "<span>Cancel</span>",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false,
        reverseButtons: true
      });

      if (!confirmUpdate.value) return;
      this.submitting = true;
      try {
        let response = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_BRAINTREE_CHANGE_ARTIST_PLAN,
          variables: {
            artist_id: artist.id,
            selected_plan: ""
          }
        });

        const success = response.data.braintree_change_artist_plan.success;
        if (success) {
          await this.$store.dispatch("snack", {
            text:
              "Your subscription for " + artist.name + " has been cancelled.",
            autoDismiss: false
          });
        } else {
          await this.$store.dispatch("snack", {
            type: "error",
            text: "Something went wrong updating your plan."
          });
        }
      } catch (error) {
        console.log(error);
        await this.$store.dispatch("snack", {
          type: "error",
          text: "Something went wrong updating your plan."
        });
      }

      await this.$apollo.queries?.user?.refetch();
      await this.$apollo.queries.get_current_user_data.refetch();
      await this.$apollo.queries.billing_information.refetch();
      this.submitting = false;
    },
    async restoreArtist(artist) {
      const confirmUpdate = await Swal.fire({
        title: `Are you sure?`,
        html: `<p class="description">You are about to restore <b style="color: #C5C5FF">${artist.name}</b>'s subscription.</p>`,
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "<span>Restore</span>",
        cancelButtonText: "<span>Cancel</span>",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false,
        reverseButtons: true
      });

      if (!confirmUpdate.value) return;
      this.submitting = true;
      try {
        let response = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_BRAINTREE_CHANGE_ARTIST_PLAN,
          variables: {
            artist_id: artist.id,
            selected_plan: artist.current_plan
          }
        });

        const success = response.data.braintree_change_artist_plan.success;
        if (success) {
          await this.$store.dispatch("snack", {
            text:
              "Your subscription for " + artist.name + " has been restored.",
            autoDismiss: false
          });
        } else {
          await this.$store.dispatch("snack", {
            type: "error",
            text: "Something went wrong updating your plan."
          });
        }
      } catch (error) {
        console.log(error);
        await this.$store.dispatch("snack", {
          type: "error",
          text: "Something went wrong updating your plan."
        });
      }
      await this.$apollo.queries.get_current_user_data.refetch();
      this.$apollo.queries.billing_information.refetch();
      this.submitting = false;
    },
    changeArtistPlan(artist) {
      this.artistToChangePlan = artist;
      this.openPlanChangeModal = true;
    },
    async closeChangeArtistPlan(reload = false) {
      this.artistToChangePlan = {};
      this.openPlanChangeModal = false;
      await this.$apollo.queries?.user?.refetch();

      if (reload) {
        await this.$apollo.queries.get_current_user_data.refetch();
        await this.$apollo.queries.billing_information.refetch();
      }
    },
    getFormattedDate(date) {
      return moment(date).format("MMM D, YYYY");
    }
  },
  beforeDestroy() {
    if (this.braintreeInstance) {
      try {
        this.braintreeInstance.teardown();
      } catch (error) {
        console.error(error);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.billing-user {
  flex: 1;
  display: flex;
  flex-direction: row;
  background: #24224a;
  border-radius: 10px;
  padding: 12px 24px;
  margin: 36px 0;

  .billing-user-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 24px;

    .title {
      font-size: 14px;
    }

    .name {
      margin-top: 10px;
      font-weight: 900;
      font-size: 18px;
      line-height: 26px;
      color: #ffffff;
    }

    .email {
      margin-top: 10px;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      color: #ffffff;
    }
  }
}

.past-due {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #aa0909;
  border-radius: 10px;
  padding: 12px 24px;
  margin-bottom: 36px;

  .title {
    font-weight: 900;
    font-size: 18px;
    line-height: 26px;
    color: #ffffff;

    i {
      margin-right: 5px;
    }
  }

  .details {
    margin-top: 10px;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: #ffffff;
  }
}

.suspended-artist {
  flex: 1;
  display: flex;
  flex-direction: row;
  background: #d6a654;
  border-radius: 10px;
  padding: 12px 24px;
  margin-bottom: 36px;

  .artist-details {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;

    .name {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: white;
      mix-blend-mode: normal;
    }
  }
}

.billing-card {
  display: grid;
  grid-gap: 24px;
  margin-bottom: 36px;
  padding: 24px;
  /* BG/type2 */
  background: #24224a;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;

  &.trial {
    grid-gap: 12px;
  }

  .card-top {
    .card-title {
      /* 18/SemiBold */
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      /* White */
      color: #ffffff;
      mix-blend-mode: normal;
      opacity: 0.9;
    }

    .card-text {
      margin: 0;
      font-size: 14px;
      line-height: 24px;
      /* Text/type0 */
      color: #b1afcd;
      mix-blend-mode: normal;
      opacity: 0.9;

      .bold {
        font-weight: 600;
        //color: white;
      }
    }

    &.card-information {
      display: flex;
      justify-content: center;
      align-items: center;

      .brands {
        margin-left: auto;
        display: flex;
        gap: 20px;
      }
    }
  }

  .card-content {
    display: grid;
    grid-gap: 24px;

    .enterprise-manage-button {
      justify-self: start;
    }

    .payment-container {
      width: 100%;

      .payment-footer {
        padding-top: 10px;
        display: flex;

        button {
          margin-left: auto;
        }
      }
    }

    .braintree-billing {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;

      .payment {
        width: 100%;

        .button-default {
          //margin-top: -44px;
          padding: 9px 30px;
          float: right;
        }
      }
    }
  }

  .next-charge {
    background: $bg-type-1;
    border-radius: 10px;
    padding: 12px 24px;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 18px;

    .date {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .span {
        /* 16/SemiBold */
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        /* White */
        color: white;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
    }

    .details {
      display: flex;
      flex-direction: column;
      text-align: right;

      .total {
        /* 16/SemiBold */
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        /* White */
        color: white;
        mix-blend-mode: normal;
        opacity: 0.9;
      }

      .subscription {
        /* 12/Regular */
        font-size: 14px;
        /* Text/type0 */
        color: $text-type-0;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
    }

    &.trial {
      padding: 30px;
    }
  }

  .billing-interval {
    display: flex;
    justify-content: space-evenly;

    .element {
      display: flex;
      flex-direction: column;
      background: $bg-type-1;
      width: 45%;
      padding: 22px 0;
      align-items: center;
      border-radius: 10px;

      &.selected {
        border: 1px solid #be3bff;
      }

      &:hover {
        cursor: pointer;
      }

      .title {
        /* 16/SemiBold */
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        /* White */
        color: white;
        mix-blend-mode: normal;
        opacity: 0.9;
      }

      .subtitle {
        /* 12/Regular */
        font-size: 14px;
        /* Text/type0 */
        color: $text-type-0;
        mix-blend-mode: normal;
        opacity: 0.9;
      }
    }

    &.trial {
      justify-content: space-between;

      .element {
        width: 47.5%;
      }
    }
  }

  .payment-container {
    width: 100%;

    .payment-footer {
      padding-top: 10px;
      display: flex;
      align-items: center;

      .due-now {
        display: flex;
        flex-direction: column;
        text-align: right;
        margin-left: auto;

        .total {
          /* 16/SemiBold */
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          /* White */
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;
        }

        .text {
          /* 12/Regular */
          font-size: 14px;
          /* Text/type0 */
          color: $text-type-0;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }

      button {
        margin-left: 20px;
      }
    }
  }

  .artists,
  .invoices {
    width: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 18px;

    .artist,
    .invoice {
      flex: 1;
      display: flex;
      flex-direction: row;
      background: $bg-type-1;
      border-radius: 10px;
      padding: 12px 24px;

      .artist-details,
      .payment-method {
        flex: 1;
        display: flex;
        flex-direction: row;
        align-items: center;

        .avatar {
          width: 50px;
          height: 50px;
          border-radius: 100px;
          margin-right: 16px;
        }

        svg {
          margin-right: 16px;
        }

        .name {
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          color: white;
          mix-blend-mode: normal;
        }

        .details {
          display: flex;
          flex-direction: column;

          .title {
            /* 16/SemiBold */
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            /* White */
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;
          }

          .subtitle {
            /* 12/Regular */
            font-size: 14px;
            /* Text/type0 */
            color: $text-type-0;
            mix-blend-mode: normal;
            opacity: 0.9;
          }
        }
      }

      &.disabled {
        background-color: #1b193c55;
        border: 1px #ffffff55 dashed;

        img {
          opacity: 0.4;
        }
      }
    }

    .subscription {
      display: flex;
      align-items: center;

      .status {
        /* 16/SemiBold */
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        /* White */
        color: white;
        mix-blend-mode: normal;
      }

      .suspended {
        color: #e3507a;
      }

      .suspended-active {
        color: #e3507a;
        font-weight: 600;
        margin-right: 5px;
      }

      .invoice-date {
        margin: 0 40px;
      }

      .details {
        display: flex;
        flex-direction: column;
        text-align: right;
        margin-right: 24px;

        .total {
          /* 16/SemiBold */
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          /* White */
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;
        }

        .subscriptions {
          /* 12/Regular */
          font-size: 14px;
          /* Text/type0 */
          color: $text-type-0;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }

      button {
        max-height: 50px;

        span {
          margin-left: 6px;
          margin-top: 2px;
        }

        svg {
          margin: 0;
        }
      }
    }
  }

  .invoices {
    max-height: 50vh;
    overflow-y: auto;
    flex-wrap: nowrap;
    padding-right: 5px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #403d84;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: #be3bff;
      border-radius: 10px;
    }

    .invoice {
      width: 100%;
    }
  }
}
</style>
